import { useTranslation } from "@impero-com/common/utils/i18n";
import { useLocalStorage } from "@impero-com/ui";
import { z } from "zod";

import { useCurrentUserOverview } from "utils/hooks/useCurrentUserOverview";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

const visibilitySchema = z.record(z.string(), z.boolean());

export function useControlHealthWidgets(isNewDashboard = true) {
    const { t } = useTranslation();
    const user = useCurrentUserOverview();
    const healthActivitiesEnabled = useFeatureFlag(
        "controlHealthScoreActivities"
    );
    const healthEnabled = !isNewDashboard || healthActivitiesEnabled;
    const [visibility, setVisibility] = useLocalStorage<
        Record<string, boolean>
    >(
        `control-health-widgets:${user?.id}`,
        {},
        {
            deserializer: (value) => visibilitySchema.parse(JSON.parse(value)),
        }
    );
    const widgets = [
        {
            id: "onTimeRate",
            label: t("ControlHealth_OnTimeRate_Label"),
            enabled: isNewDashboard,
            kpi: true,
        },
        {
            id: "completionRate",
            label: t("ControlHealth_CompletionRate_Label"),
            enabled: isNewDashboard,
            kpi: true,
        },
        {
            id: "frictionRate",
            label: t("ControlHealth_FrictionRate_Label"),
            enabled: isNewDashboard && healthEnabled,
            kpi: true,
        },
        {
            id: "activities",
            label: t("ControlHealth_Activities_Title"),
            enabled: isNewDashboard,
            kpi: true,
        },
        {
            id: "activityTrend",
            label: t("ControlHealth_ActivityTrend_Title"),
            enabled: healthEnabled,
            kpi: false,
        },
        {
            id: "frictionBreakdown",
            label: t("ControlHealth_FrictionBreakdown_Title"),
            enabled: healthEnabled,
            kpi: false,
        },
        {
            id: "statusBreakdown",
            label: t("ControlHealth_StatusBreakdown_Title"),
            enabled: true,
            kpi: false,
        },
    ]
        .filter((widget) => widget.enabled)
        .map((widget) => ({
            ...widget,
            visible: !isNewDashboard || visibility[widget.id] !== false,
        }));

    return {
        widgets,
        setVisible: (id: string, visible: boolean) =>
            setVisibility((previous) => ({ ...previous, [id]: visible })),
        isVisible: (id: string) =>
            widgets.some((widget) => widget.id === id && widget.visible),
    };
}
